import React, { useState } from 'react';
import { FileText, Download, RefreshCw } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../api';

const TIPO_LABEL = { servicios: 'Servicios', reembolsos: 'Reembolsos' };

export default function FacturarButton({ liquidacionId, facturasElectronicas, onFacturada }) {
  const [cargando, setCargando] = useState(false);

  const fes = Array.isArray(facturasElectronicas) ? facturasElectronicas : [];

  const emitir = async () => {
    setCargando(true);
    try {
      const res = await api.post('/facturas-electronicas/emitir/', {
        liquidacion_id: liquidacionId,
      });
      const facturas = Array.isArray(res.data) ? res.data : [res.data];
      if (facturas.length === 0) {
        toast.success('Ya estaba todo facturado — no había nada pendiente.');
      } else {
        const autorizadas = facturas.filter((f) => f.estado === 'autorizada');
        if (autorizadas.length === facturas.length) {
          toast.success(`${facturas.length} factura(s) autorizada(s)`);
        } else {
          toast.success('Facturas emitidas — revisa el estado de cada una.');
        }
      }
      if (onFacturada) onFacturada(facturas);
    } catch (err) {
      const data = err.response?.data;
      const msg = data?.error || 'Error al emitir factura.';
      const errores = data?.errores_sri || [];
      // Aunque un tipo (servicios/reembolsos) haya fallado, el otro puede
      // haberse emitido bien — no perder ese resultado solo porque la
      // llamada terminó en error.
      if (data?.facturas?.length && onFacturada) onFacturada(data.facturas);
      if (errores.length > 0) {
        const detalles = errores.map((e) => e.mensaje || e.informacionAdicional).join('; ');
        toast.error(`${msg}\n${detalles}`, { duration: 8000 });
      } else {
        toast.error(msg);
      }
    } finally {
      setCargando(false);
    }
  };

  const descargarXml = async (factura) => {
    try {
      const res = await api.get(`/facturas-electronicas/${factura.id}/xml_autorizado/`, {
        responseType: 'blob',
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${factura.tipo || factura.id}.xml`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo descargar el XML.');
    }
  };

  const descargarPdf = async (factura) => {
    try {
      const res = await api.get(`/facturas-electronicas/${factura.id}/ride/`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = `factura_${factura.tipo || factura.id}.pdf`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      toast.error('No se pudo generar el PDF.');
    }
  };

  const autorizadas = fes.filter((f) => f.estado === 'autorizada');
  const algunaRechazada = fes.some((f) => f.estado === 'rechazada');

  // Importante: NO asumimos "ya está todo hecho" solo porque todo lo que hay
  // en `fes` está autorizado — una liquidación puede tener servicios Y
  // reembolsos, y si uno de los dos nunca se llegó a intentar (bug real que
  // pasó: un tipo rechazado abortaba el otro antes de emitirlo), `fes` solo
  // trae UNO y parecería "completo" sin estarlo. Por eso el botón para
  // (re)intentar SIEMPRE está disponible — emitir() ya es seguro de repetir:
  // el backend salta los tipos que ya están vigentes, así que no duplica cobros.
  return (
    <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', alignItems: 'center' }}>
      {autorizadas.map((f) => (
        <React.Fragment key={f.id}>
          <button
            className="btn-ghost btn-sm"
            onClick={() => descargarXml(f)}
            title={`Descargar XML ${TIPO_LABEL[f.tipo] || f.tipo}`}
            style={{ color: 'var(--color-success)' }}
          >
            <Download size={14} /> {TIPO_LABEL[f.tipo] || 'XML'}
          </button>
          <button
            className="btn-ghost btn-sm"
            onClick={() => descargarPdf(f)}
            title={`Descargar PDF ${TIPO_LABEL[f.tipo] || f.tipo}`}
            style={{ color: 'var(--color-success)' }}
          >
            <FileText size={14} /> PDF
          </button>
        </React.Fragment>
      ))}
      <button
        className="btn-ghost btn-sm"
        onClick={emitir}
        disabled={cargando}
        title={
          fes.length === 0
            ? 'Emitir facturas electrónicas'
            : 'Verificar / completar facturación — no duplica lo ya autorizado'
        }
        style={algunaRechazada ? { color: 'var(--color-warning)' } : undefined}
      >
        {cargando ? (
          <RefreshCw size={14} className="spin" />
        ) : algunaRechazada ? (
          <RefreshCw size={14} />
        ) : (
          <FileText size={14} />
        )}
        {' '}
        {cargando
          ? 'Facturando...'
          : fes.length === 0
            ? 'Facturar'
            : algunaRechazada
              ? 'Reintentar'
              : 'Verificar facturación'}
      </button>
    </div>
  );
}
